import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/roboto-mono/400.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './App';

/**
 * In mock mode the entire app runs against MSW-intercepted fixtures
 * (brainstorm.md 1.13). MSW intercepts at the network layer, so the real fetch
 * code in lib/canvas/client.ts is exercised rather than bypassed.
 */
async function enableMocking(): Promise<void> {
  if (import.meta.env.VITE_CANVAS_MODE === 'live') return;

  const { worker } = await import('./mocks/browser');
  await worker.start({
    onUnhandledRequest: 'bypass',
    quiet: true,
    serviceWorker: { url: '/mockServiceWorker.js' },
  });
}

/**
 * Register the PWA service worker (phase 8).
 *
 * Only in live mode, and this is not an oversight: MSW registers its own
 * service worker in mock mode, and only one service worker can control a page
 * at a given scope. Registering both means they fight for control and the mock
 * data stops being served. The app is still installable in mock mode — MSW's
 * worker satisfies the "has a fetch handler" requirement — it just does not get
 * the offline app-shell cache until live mode is switched on.
 */
function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  if (import.meta.env.VITE_CANVAS_MODE !== 'live') return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((error: unknown) => {
      // Never fatal: a failed registration costs offline support and nothing else.
      console.warn('[C-GC] Service worker registration failed:', error);
    });
  });
}

const container = document.getElementById('root');
if (!container) throw new Error('Root container #root is missing from index.html');

registerServiceWorker();

void enableMocking().then(() => {
  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
});
